/**
 * Client Management Zod Validation Schemas
 *
 * Task: T-P5-D7-F1-1 (Foundation)
 *
 * Zod schemas for request validation. Uses z.coerce.number() for query
 * params since Fastify delivers them as strings from the query string.
 */

import { z } from 'zod';

const windowDaysSchema = z.union([z.literal(30), z.literal(60), z.literal(90), z.literal(180)]);
const bookedWindowDaysSchema = z.union([z.literal(30), z.literal(60), z.literal(90), z.literal(180), z.literal(360)]);

export const clientListQuerySchema = z.object({
  q: z.string().optional(),
  searchMode: z.enum(['name', 'username', 'phone', 'email']).optional(),
  sortBy: z
    .enum([
      'name',
      'registeredAt',
      'lastActive',
      'sessionCount',
      'futureBookings',
      'lastBookedAt',
      'lastBookingCreatedAt',
    ])
    .default('futureBookings'),
  sortDir: z.enum(['asc', 'desc']).default('asc'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  accessStatus: z.enum(['SUSPENDED', 'BANNED', 'none']).optional(),
  accountType: z.enum(['telegram', 'external']).optional(),
  serviceIds: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) return undefined;
      const parts = val.split(',').map((s) => s.trim()).filter(Boolean);
      return parts.length > 0 ? parts : undefined;
    })
    .pipe(z.array(z.string().uuid()).optional()),
  minSessions: z.coerce.number().int().min(0).optional(),
  maxSessions: z.coerce.number().int().min(0).optional(),
  registeredAfter: z.string().datetime().optional(),
  registeredBefore: z.string().datetime().optional(),
  lastActiveAfter: z.string().datetime().optional(),
  lastActiveBefore: z.string().datetime().optional(),
  tagIds: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) return undefined;
      const parts = val.split(',').map((s) => s.trim()).filter(Boolean);
      return parts.length > 0 ? parts : undefined;
    })
    .pipe(z.array(z.string().uuid()).optional()),
  tagMatchMode: z.enum(['ANY', 'ALL']).default('ALL'),
  formId: z.string().uuid().optional(),
  metricMode: z.enum(['total', 'lastActive', 'new', 'booked', 'inactive']).optional(),
  newWindowDays: z.coerce.number().pipe(windowDaysSchema).optional().default(30),
  lastActiveWindowDays: z.coerce.number().pipe(windowDaysSchema).optional().default(30),
  bookedWindowDays: z.coerce.number().pipe(bookedWindowDaysSchema).optional().default(30),
});

export const createExternalClientSchema = z.object({
  firstName: z.string().min(1).max(100),
  lastName: z.string().max(100).optional(),
  phone: z.string().max(30).optional(),
  email: z.string().email().optional(),
  telegramUsername: z.string().max(100).optional(),
});

export const updateClientProfileSchema = z.object({
  firstName: z.string().min(1).max(100).optional(),
  lastName: z.string().max(100).optional(),
  // service-by-phone: a saved number is never cleared here; a phone of only spaces is blank.
  phone: z.string().trim().min(1).max(32).optional(),
  email: z.string().email().optional(),
});

export const paginationQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export const statusTransitionSchema = z.object({
  to: z.enum(['SUSPENDED', 'BANNED']).nullable(),
  reason: z.string().max(500).optional(),
});

// `bulkDeleteRequestSchema` was removed with the bulk-delete endpoint
// (client-lifecycle-integrity). Removal is single-client only.

export const mergeClientRequestSchema = z.object({
  sourceClientId: z.string().uuid(),
});

// ============================================================================
// Parsed Output Types (post-Zod-transform)
// ============================================================================

export type ClientListQueryInput = z.input<typeof clientListQuerySchema>;
export type ClientListQueryParsed = z.output<typeof clientListQuerySchema>;

export const clientCountQuerySchema = clientListQuerySchema.omit({
  cursor: true,
  limit: true,
  sortBy: true,
  sortDir: true,
});
export type ClientCountQueryParsed = z.output<typeof clientCountQuerySchema>;

// ============================================================================
// Client List Filters Schema (T-P5-D7-F4-1)
// ============================================================================

export const clientListFiltersSchema = clientListQuerySchema.omit({
  cursor: true,
  limit: true,
  sortBy: true,
  sortDir: true,
});

export type ClientListFiltersParsed = z.output<typeof clientListFiltersSchema>;

// ============================================================================
// Bulk Client Selection Schema (T-P5-D7-F4-1)
// ============================================================================

export const explicitSelectionSchema = z.object({
  mode: z.literal('explicit'),
  clientIds: z.array(z.string().uuid()).min(1).max(50),
});

export const allFilteredSelectionSchema = z.object({
  mode: z.literal('all_filtered'),
  filters: clientListFiltersSchema,
});

export const bulkClientSelectionSchema = z.discriminatedUnion('mode', [
  explicitSelectionSchema,
  allFilteredSelectionSchema,
]);

// ============================================================================
// Communication Action Request Schemas (T-P5-D7-F4-1)
// ============================================================================

export const inviteRequestSchema = z.object({
  targetType: z.enum(['session', 'event']),
  targetId: z.string().uuid(),
  message: z.string().max(500).optional(),
  idempotencyKey: z.string().max(128).optional(),
});

export const formRequestRequestSchema = z.object({
  formId: z.string().uuid(),
  message: z.string().max(500).optional(),
  /** chat-composer-attach-preview D1: the chat note is delivered as typed, without {firstName}. */
  literalMessage: z.boolean().optional(),
  idempotencyKey: z.string().max(128).optional(),
  targetType: z.enum(['session', 'event']).optional(),
  targetId: z.string().uuid().optional(),
}).refine(
  (data) => {
    // Both must be present or both absent
    if (data.targetType && !data.targetId) return false;
    if (!data.targetType && data.targetId) return false;
    return true;
  },
  { message: 'targetType and targetId must both be provided or both omitted', path: ['targetId'] },
);

export const csvExportRequestSchema = z.object({
  selection: bulkClientSelectionSchema,
});
